import * as yup from 'yup';
import { BANKING_INFO_MESSAGE, NEW_BANK } from 'global/constants';
import { Bank } from '../../../../../../../global/entities/SellOrder';
import {
  SellOrganizationOwnerForm,
  SellPersonOwnerForm,
} from '../owners-agreements/owners-agreements-types';
import { BankKind } from '../../../../sell-process-types';

const createFieldAccessor = (fieldName: string) => ({
  base: fieldName,
  sellerField: (index: number) =>
    `${BankingFieldName.sellers}[${index}].${fieldName}`,
});

export const BankingFieldName = {
  clientFundsAccount: 'clientFundsAccount',
  acquiredCash: 'acquiredCash',
  bank: 'bank',

  newBank: createFieldAccessor('newBank'),
  bankAccount: createFieldAccessor('bankAccount'),
  bankInfo: createFieldAccessor('bankInfo'),

  socialSecurityNumber: createFieldAccessor('socialSecurityNumber'),
  organizationNumber: createFieldAccessor('organizationNumber'),

  sellers: 'sellers',
};

export const BankingFieldLabel = {
  acquiredCash: 'Förvärvad kassa',
  bank: 'Bank',
  bankAccount: 'Bankkonto',
  clientFundsAccount: 'Klientmedelskonto',
  bankInfo: 'Banknamn',
  newBank: 'Bank annan',
  sellers: 'Säljare',
};

export const BankingCbsAccount = {
  [Bank.HandelsBanken]: 'Handelsbanken, 6143 941 588 998',
  [Bank.SEB]: 'SEB, 5330 10 146 31',
};

export const isPersonSeller = (
  seller: SellPersonOwnerForm | SellOrganizationOwnerForm,
): seller is SellPersonOwnerForm => 'socialSecurityNumber' in seller;

export const sellBankingValidationSchema = yup.object().shape({
  [BankingFieldName.bankAccount.base]: yup.string().nullable(),
  [BankingFieldName.clientFundsAccount]: yup.string().nullable(),
  [BankingFieldName.bank]: yup
    .object()
    .required()
    .test(
      'fieldError',
      'Klicka på "Lägg till ny bank" för att spara ny bank.',
      function validateBank(value) {
        if (!this.parent.newBank || !value) return true;
        // @ts-ignore
        return value.id !== NEW_BANK;
      },
    ),
  [BankingFieldName.acquiredCash]: yup.number().nullable(),
  [BankingFieldName.newBank.base]: validateNewBankInput(),
  sellers: yup.array().of(
    yup.object().shape({
      [BankingFieldName.newBank.base]: validateNewBankInput(),
      bankAccount: yup
        .string()
        .nullable()
        .test(
          'Error',
          'Bankkonto är obligatoriskt när ett banknamn är angivet.',
          function validate(value) {
            const dependentValue = this.parent.bankInfo;
            if (!dependentValue && !value) {
              return true;
            }

            if (dependentValue && dependentValue.id === NEW_BANK && !value) {
              return true;
            }

            if (dependentValue && dependentValue.id !== NEW_BANK && !value) {
              return false;
            }

            if (!dependentValue && value) {
              return true;
            }

            if (dependentValue && value) {
              return true;
            }

            return false;
          },
        ),
      bankInfo: yup
        .object()
        .nullable()
        .test(
          'fieldError',
          'Klicka på "Lägg till ny bank" för att spara ny bank.',
          function validateSellerBank(value) {
            if (!this.parent.newBank || !value) return true;
            // @ts-ignore
            return value.id !== NEW_BANK;
          },
        )
        .concat(
          sellerInfoFieldValidate(
            'bankAccount',
            'Banknamn är obligatoriskt när ett kontonummer är angivet.',
          ),
        ),
    }),
  ),
});

function validateNewBankInput() {
  return yup
    .string()
    .nullable()
    .test('error', BANKING_INFO_MESSAGE, (value) => {
      if (value) {
        // @ts-ignore
        return value ? value?.split(',').length === 3 : false;
      }
      return true;
    });
}

function sellerInfoFieldValidate(dependentField: string, message: string) {
  return yup
    .object()
    .nullable()
    .test(`${dependentField}Required`, message, function validate(value) {
      const dependentValue = this.parent[dependentField];
      if (!dependentValue && !value) {
        return true;
      }

      if (dependentValue && !value) {
        return false;
      }

      if (!dependentValue && value) {
        return true;
      }

      if (dependentValue && value) {
        return true;
      }

      return false;
    });
}

export const getCbsBank = <T extends { bankType: BankKind }>(banks: T[]) =>
  banks.find((bank) => bank.bankType === BankKind.Internal);

export const getClientBank = <T extends { bankType: BankKind }>(banks: T[]) =>
  banks.find((bank) => bank.bankType === BankKind.Company);
